import React, { useEffect, useReducer } from "react";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import { FaPlus, FaTrashAlt } from "react-icons/fa";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";

import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { TFunction } from "i18next";
import { U2000IdColumnDef, type U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import u2000FormState, { U2000FormStateEnumState, type U2000FormState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import useU2000AxiosAuthenticated from "../../../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import type { U2103UserFormExtendedHandlers } from "../../../U2103UserForm";
import { NS, U2103ROLES } from "../../../../U2103";
import { emptyUser, type U2103User, type U2103UserRole } from "../../../../U2103Service";
import { u2103UserRoleHandler } from "./U2103UserRoleHandlers";
import U2103UserRoleEditDlg from "./U2103UserRoleEditDlg";
import U2103UserRoleDeleteDlg from "./U2103UserRoleDeleteDlg";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";

// Typage des props du composant
interface U2103UserRolesProps {
  u2103UserFormHandlers: U2103UserFormExtendedHandlers;
}

// Définition des colonnes pour le tableau des actions d'une unité logique
function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: "roleCode",
      label: t("lblRoleCode", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[15%]",
      type: "string",
      align: "left",
    },
    {
      name: "roleDescription",
      label: t("lblRoleDescription", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      type: "string",
      align: "left",
    },
  ];
}

// Composant principal pour la gestion des actions d'une unité logique
const U2103UserRolesBody: React.FC<U2103UserRolesProps> = ({ u2103UserFormHandlers }) => {
  // Translation
  const { t } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  const gridStateHandlers = useU2000GridStateHandlers<U2103UserRole>();

  const validateUserRole = (_user: U2103User) => {
    const result: Record<string, string> = {};
    return result;
  };

  const u2103UserRoleBL: U2000FormSBusinessHandlers<U2103User> = {
    newRecord: () => {
      return emptyUser;
    },

    // validation du formulaire
    validateForm: validateUserRole,
  };

  const u2103UserRoleformStateHandlers = useU2000FormStateHandlers<U2103User>({
    formState,
    formStateDispatch,
    businessHandlers: u2103UserRoleBL,
  });

  const u2103UserRoleHandlers = u2103UserRoleHandler({
    u2000AxiosAuthenticated,
    gridStateHandlers: gridStateHandlers as unknown as ReturnType<typeof useU2000GridStateHandlers<U2103UserRole>>,
    t,
    formStateHandlers: u2103UserRoleformStateHandlers,
  });

  // Transposer les données du formStateHandlers au gridStateHandlers pour LogicalUnitActions
  useEffect(() => {
    // Initialiser les colonnes et les données du tableau
    const columns = getColumnsDef(t);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" })));

    // Initialiser les données du tableau
    const userRoles = u2103UserFormHandlers.getData?.()?.roles as U2103UserRole[];
    gridStateHandlers.setData(Array.isArray(userRoles) ? userRoles : []);
    gridStateHandlers.setTotalRows(Array.isArray(userRoles) ? userRoles.length : 0);

    // Gérer les erreurs spécifiques au champ LogicalUnitActions
    const errorsField = u2103UserFormHandlers.getOtherErrors()?.roleActions;
    if (errorsField !== undefined) {
      gridStateHandlers.setErrors(Array.isArray(errorsField) ? errorsField : errorsField ? [errorsField] : []);
    } else {
      gridStateHandlers.setErrors([]);
    }
  }, [u2103UserFormHandlers, t]);

  // Mettre à jour le formStateHandlers parent lorsque les données du gridStateHandlers changent
  useEffect(() => {
    if (u2103UserRoleHandlers.dataChangedEvent === 0) return;

    const userRoles = gridStateHandlers.getData();
    u2103UserFormHandlers.setData({
      ...u2103UserFormHandlers.getData?.(),
      roles: userRoles,
    });
  }, [u2103UserRoleHandlers.dataChangedEvent]);

  // Désactiver le bouton de suppression si aucune ligne sélectionnée
  function disableDeleteBtn(): boolean {
    const selectedRows = gridStateHandlers.getSelectedRows();
    return u2103UserFormHandlers.getFormState?.() === U2000FormStateEnumState.READ || selectedRows.length === 0 ? true : false || "resetPwd" in (u2103UserFormHandlers.getCustomAttributes?.() ?? {});
  }

  // Préparer l'array d'actions pour le toolbar responsive
  const actions = React.useMemo(
    () => [
      {
        key: "add",
        label: t("btnAdd", { ns: NS }),
        handleClick: u2103UserRoleHandlers.onAdd,
        // changed to "not in" semantics: only true if hasRole AND resetPwd is NOT present
        hasRole: hasRole(U2103ROLES.ROLES),
        disabled: u2103UserFormHandlers.getFormState?.() === U2000FormStateEnumState.READ || "resetPwd" in (u2103UserFormHandlers.getCustomAttributes?.() ?? {}),
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "delete",
        label: t("btnDelete", { ns: NS }),
        handleClick: u2103UserRoleHandlers.onDelete,
        hasRole: hasRole(U2103ROLES.ROLES),
        disabled: disableDeleteBtn(),
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    [t, u2103UserRoleHandlers]
  );

  // Render
  return (
    <>
      <U2103UserRoleEditDlg u2103UserRoleHandler={u2103UserRoleHandlers} t={t} NS={NS} />
      <U2103UserRoleDeleteDlg u2103UserRoleHandler={u2103UserRoleHandlers} t={t} NS={NS} />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        allowRowSelection={u2103UserFormHandlers.getFormState?.() === U2000FormStateEnumState.EDIT}
        label={t("lblUserRole", {
          count: u2103UserRoleHandlers.gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
      />
    </>
  );
};

export default U2103UserRolesBody;
